import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DataCollectionMappingConfiguration } from '../../../teams/models/data-collection-mapping-configuration.interface';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { CollectedFields } from './collected-fields';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { TranslationHandleService } from '../../../../core/translate/translation-handle.service';
import { SourcesFormChangesService } from '../../services/sources-form-changes.service';
import { Subject } from 'rxjs';

const PHRASES_FOR_TRANSLATE = [
  {
    Date: 'teams.dataCollectionMapping.CollectedFields.Fields.Date',
  },
  {
    UserName: 'teams.dataCollectionMapping.CollectedFields.Fields.User',
  },
  {
    TeamName: 'teams.dataCollectionMapping.CollectedFields.Fields.ProjectName',
  },
];

@Component({
  selector: 'mee-dc-fields-mapping',
  templateUrl: './dc-fields-mapping.component.html',
  styleUrls: ['./dc-fields-mapping.component.scss'],
})
export class DcFieldsMappingComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private translationService = inject(TranslationHandleService);
  private sourcesFormChangesService = inject(SourcesFormChangesService);
  private _dataCollectionMappingConfiguration: DataCollectionMappingConfiguration;
  private destroy$: Subject<void> = new Subject();

  collectedFields = CollectedFields;
  translationArray: Map<string, string>;
  configurationForm = this.fb.group({
    configurationFields: this.fb.array([]),
  });

  @Output() onMappingFieldsChange: EventEmitter<DataCollectionMappingConfiguration> = new EventEmitter();

  @Input() set dataCollectionMappingConfiguration(dataCollectionMappingConfiguration: DataCollectionMappingConfiguration) {
    this._dataCollectionMappingConfiguration = dataCollectionMappingConfiguration;
  }

  get configurationFields(): FormArray {
    return this.configurationForm.get('configurationFields') as FormArray;
  }

  get isAllFieldExist(): boolean {
    return Object.values(CollectedFields).length === this.getExistFields().length;
  }

  ngOnInit(): void {
    this.translationService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);
    this.translationService.getTranslationMap().subscribe(res => (this.translationArray = res));
    this.onFormChange();
    this.initConfigurationFields();
    this.initCancelChangesHandler();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isFieldDisable(field: string): boolean {
    return this.getExistFields().includes(field);
  }

  addFields(): void {
    const collectedFieldName = this.getAvailableField();
    const remapForm = this.createPairFields(collectedFieldName, '');

    this.configurationFields.push(remapForm);
  }

  removeField(index: number): void {
    this.configurationFields.removeAt(index);
  }

  getCollectedFieldText(field: string): string {
    return this.translationArray.get(field);
  }

  private initConfigurationFields(): void {
    this.configurationFields.clear();

    for (const field in this._dataCollectionMappingConfiguration) {
      if (this._dataCollectionMappingConfiguration.hasOwnProperty(field)) {
        if (this._dataCollectionMappingConfiguration[field] !== null) {
          const remapForm = this.createPairFields(field, this._dataCollectionMappingConfiguration[field]);
          this.configurationFields.push(remapForm);
        }
      }
    }
  }

  private onFormChange(): void {
    this.configurationForm.valueChanges
      .pipe(
        tap(() => {
          const configurationFieldsArray = this.configurationFields.controls.map(control => {
            const form = control as FormGroup;

            const collectedField = form.get('collectedField').value;
            const remapValue = form.get('remapValue').value;

            return { [collectedField]: remapValue };
          });

          const configurationFields = Object.assign({}, ...configurationFieldsArray);

          this.onMappingFieldsChange.emit(configurationFields);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private getExistFields(): string[] {
    return this.configurationFields.controls.map(control => {
      const form = control as FormGroup;

      if (form) {
        return form.value.collectedField;
      }
    });
  }

  private getAvailableField(): string {
    return Object.values(CollectedFields).find(field => this.getExistFields().indexOf(field) === -1);
  }

  private createPairFields(fieldName: string, value: string): FormGroup {
    return this.fb.group({
      collectedField: fieldName,
      remapValue: value,
    });
  }

  private initCancelChangesHandler(): void {
    this.sourcesFormChangesService.isNeedToCancelChanges$
      .pipe(
        filter(isNeedToCancel => isNeedToCancel),
        tap(() => {
          this.configurationFields.clear();
          this.initConfigurationFields();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
